import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Box,
  LinearProgress,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// -- If you have your own "determineNewLevel" logic, keep it as is.
import { determineNewLevel } from "../../utils/levelUtils.js";

/** Shake animation for empty input */
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
  20%, 40%, 60%, 80% { transform: translateX(10px); }
`;

/** Floating animation for level change */
const floatUp = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-50px);
  }
`;

/** Level Descriptions (adjust as needed) */
const LEVEL_DESCRIPTIONS = {
  1: "Apprentice 1",
  2: "Apprentice 2",
  3: "Apprentice 3",
  4: "Guru 1",
  5: "Guru 2",
  6: "Master",
  7: "Enlightened",
  8: "Burned",
};

/** Styled component for floating level change */
const FloatingLevelChange = styled(Box)(({ theme, ispositive }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 999,
  // Green if level increased, red if decreased (or same)
  color: ispositive === "true" ? "#2e7d32" : "#d32f2f",
  animation: `${floatUp} 1.5s ease-out forwards`,
  display: "flex",
  alignItems: "center",
  fontWeight: "bold",
}));

const shuffleArray = (array) => {
  return array
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);
};

const LearnModeParameterized = ({ token, cards = [], deck, onComplete }) => {
  for (let card of cards) {
    if (card.title) {
      card.question = card.title;
    }
  }
  const [shuffledCards, setShuffledCards] = useState([]);
  const [currentQueue, setCurrentQueue] = useState([]);
  const [incorrectQueue, setIncorrectQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentCard, setCurrentCard] = useState(null);

  const [userInput, setUserInput] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const [feedback, setFeedback] = useState(null);
  const [learningComplete, setLearningComplete] = useState(false);
  const [progress, setProgress] = useState(0);

  const [currentLevel, setCurrentLevel] = useState(1);
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);

  const [emptyInputError, setEmptyInputError] = useState(false);
  const [levelChangeInfo, setLevelChangeInfo] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    const shuffled = shuffleArray(cards);
    setShuffledCards(shuffled);
    setCurrentQueue(shuffled);
    setCurrentCard(shuffled[0] || null);
  }, [cards]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentIndex, currentQueue]);

  useEffect(() => {
    if (learningComplete) {
      onComplete();
    }
  }, [learningComplete, onComplete]);

  /** Handle pressing "Enter" */
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!isSubmitted) {
        handleSubmit();
      } else {
        handleProceed();
      }
    }
  };

  /** Evaluate answer */
  const handleSubmit = () => {
    if (isSubmitted) return;

    // Check for empty input
    if (!userInput.trim()) {
      setEmptyInputError(true);
      setTimeout(() => setEmptyInputError(false), 2000);
      return;
    }

    // Answer validation

    const correctAnswers = currentCard?.answer
      ?.split(',')
      .map(ans => ans.trim().toLowerCase()); // Split by comma and normalize

    const userAnswer = userInput.trim().toLowerCase();
    const isCorrect = correctAnswers.includes(userAnswer); // Check if user's answer matches any correct answer
    const newIncorrectAttempts = isCorrect ? incorrectAttempts : incorrectAttempts + 1;


    // Determine the new level based on correctness + attempts
    const newLevel = determineNewLevel(currentLevel, newIncorrectAttempts);

    // Always set floating-level info, so it re-triggers for each submission
    setLevelChangeInfo({
      oldLevel: LEVEL_DESCRIPTIONS[currentLevel],
      newLevel: LEVEL_DESCRIPTIONS[newLevel],
      isPositive: newLevel > currentLevel,
    });
    // Clear the floating message after 1.5s
    setTimeout(() => setLevelChangeInfo(null), 1500);

    // If correct
    if ( correctAnswers.includes(userAnswer)) {
      setIsCorrect(true);
      setProgress((prev) => Math.min(100, prev + 100 / shuffledCards.length));
      setFeedback({ type: "success", message: "Correct!" });
      setIncorrectAttempts(0);
    } else {
      // If incorrect
      setIsCorrect(false);
      setFeedback({ type: "error", message: "Incorrect!" });
      setIncorrectQueue((prev) => [...prev, currentCard]);
    }

    // Update states after evaluating
    setCurrentLevel(newLevel);
    setIncorrectAttempts(newIncorrectAttempts);
    setIsSubmitted(true);
  };

  /** Move on to the next card */
  const handleProceed = () => {
    if (!isSubmitted) return;

    // Reset level-change animation for next question
    setLevelChangeInfo(null);

    let newQueue = [...currentQueue];
    // Remove the current card from the queue
    newQueue.splice(currentIndex, 1);

    if (newQueue.length === 0 && incorrectQueue.length === 0) {
      setLearningComplete(true);
      onComplete();
      return;
    }

    // If queue is empty but we do have incorrect items, shuffle and retry those
    if (newQueue.length === 0) {
      newQueue = shuffleArray(incorrectQueue);
      setIncorrectQueue([]);
    }

    // Move to the next question
    setCurrentQueue(newQueue);
    setCurrentIndex(0);
    setCurrentCard(newQueue[0] || null);

    setUserInput("");
    setFeedback(null);
    setIsSubmitted(false);
    setIsCorrect(false);
  };

  // console.log(cards)

  return (
    <Container
      sx={{
        textAlign: "center",
        maxWidth: "50%",
        margin: "auto",
        position: "relative", // for the floating-level element
        overflow: "visible",
      }}
    >

      {/* Progress Bar + Current Level Display */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: "#e0e0e0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#4caf50",
              },
            }}
          />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">
            {`${Math.round(progress)}%`}
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="h5"
        sx={{
          backgroundColor: "#e3f2fd",
          textAlign: "center",
          p: 2,
          borderRadius: 1,
          mb: 2,
        }}
      >
        Current Level: {LEVEL_DESCRIPTIONS[currentLevel]}
      </Typography>

      {learningComplete ? (
        <Alert severity="success">You have successfully learned all new cards!</Alert>
      ) : currentQueue.length === 0 ? (
        <Alert severity="info">No new cards available for learning.</Alert>
      ) : (
        <Card
          sx={{
            boxShadow: 3,
            p: 3,
            position: "relative",
            overflow: "visible",
            mb: 8,
            backgroundColor: isSubmitted
              ? isCorrect
                ? "#e8f5e9"
                : "#ffebee"
              : "white",
          }}
        >
          <CardContent>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {currentQueue.length > 0
                ? currentCard?.question || "Loading..."
                : "No more questions!"}
            </Typography>

            <TextField
              fullWidth
              label="Your Answer"
              variant="outlined"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{ readOnly: isSubmitted }}
              inputRef={inputRef}
              sx={{
                "& .MuiOutlinedInput-root": {
                  animation: emptyInputError ? `${shake} 0.5s ease-in-out` : "none",
                },
              }}
            />

            {/* Show error if user tries to submit empty input */}
            {emptyInputError && (
              <Typography
                variant="body2"
                color="error"
                sx={{ mt: 1, fontStyle: "italic" }}
              >
                Thou shall not pass without answering!
              </Typography>
            )}

            {/* Submit/Proceed button */}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={isSubmitted ? handleProceed : handleSubmit}
                sx={{
                  backgroundColor: isSubmitted ? "#1976d2" : "#4caf50",
                  "&:hover": {
                    backgroundColor: isSubmitted ? "#1565c0" : "#388e3c",
                  },
                }}
              >
                {isSubmitted ? "Proceed" : "Submit"}
              </Button>
            </Box>

            {/* Feedback icons/text */}
            {isSubmitted && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: isCorrect ? "#2e7d32" : "#d32f2f",
                }}
              >
                {isCorrect ? (
                  <>
                    <CheckCircleOutlineIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">Great job!</Typography>
                  </>
                ) : (
                  <>
                    <HighlightOffIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">Keep trying!</Typography>
                  </>
                )}
              </Box>
            )}

            {/* Light blue alert showing correct answer if incorrect */}
            {isSubmitted && !isCorrect && (
              <Alert
                sx={{
                  mt: 2,
                  backgroundColor: "#e3f7ff",
                  color: "#333",
                  fontWeight: "bold",
                }}
              >
                Correct answer: {currentCard?.answer}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default LearnModeParameterized;
